import React from "react";

interface PrivacyPolicyContentProps {
  compact?: boolean;
}

export const PrivacyPolicyContent: React.FC<PrivacyPolicyContentProps> = ({ compact = false }) => {
  const headingClass = compact ? "text-base font-semibold mb-2" : "text-lg font-semibold mb-3";
  const subHeadingClass = compact ? "font-medium mb-1 text-sm" : "font-medium mb-2";
  const textClass = "text-muted-foreground leading-relaxed";
  const smallTextClass = compact ? "text-muted-foreground text-sm" : "text-muted-foreground leading-relaxed";

  return (
    <div className="space-y-6 text-sm">
      <div className="text-xs text-muted-foreground">
        Last updated: 2025-12-06
      </div>

      <p className={textClass}>
        This Privacy Policy explains how Straiv ("we", "our", "the app") collects, uses, stores, and protects your personal information when you use our mobile application and services.
      </p>

      <p className={textClass}>
        By creating an account or using Straiv, you agree to the terms in this Privacy Policy.
      </p>

      {/* Section 1 */}
      <section>
        <h3 className={headingClass}>1. Information We Collect</h3>
        <p className={smallTextClass + " mb-3"}>
          We collect the following types of data to provide our services:
        </p>

        <div className="space-y-4 ml-4">
          <div>
            <h4 className={subHeadingClass}>1.1 Account Information</h4>
            <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-sm">
              <li>Name</li>
              <li>Email address</li>
              <li>Password (encrypted)</li>
              <li>Authentication tokens</li>
              <li>Verification status</li>
            </ul>
          </div>

          <div>
            <h4 className={subHeadingClass}>1.2 Fitness & Health Data You Provide</h4>
            <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-sm">
              <li>Workout logs, routines, sets, reps, weights</li>
              <li>Cardio and running logs</li>
              <li>Goals and preferences</li>
              <li>Body measurements</li>
              <li>Notes about injuries or limitations</li>
            </ul>
          </div>

          <div>
            <h4 className={subHeadingClass}>1.3 AI Chat Data</h4>
            <p className="text-muted-foreground text-sm mb-2">We store:</p>
            <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-sm">
              <li>Messages exchanged with our AI coach</li>
              <li>Insights derived from messages (preferences, habits, goals)</li>
            </ul>
            <p className="text-muted-foreground text-sm mt-2">
              This is required to provide personalized coaching and recommendations.
            </p>
          </div>

          <div>
            <h4 className={subHeadingClass}>1.4 Automatically Collected Data</h4>
            <p className="text-muted-foreground text-sm mb-2">We may automatically collect:</p>
            <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-sm">
              <li>Device type (e.g., iPhone model)</li>
              <li>Operating system version</li>
              <li>App version</li>
              <li>Crash logs</li>
              <li>Usage analytics</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Section 2 */}
      <section>
        <h3 className={headingClass}>2. How We Use Your Information</h3>
        <p className={smallTextClass + " mb-3"}>We use your data to:</p>

        <div className="space-y-4 ml-4">
          <div>
            <h4 className={subHeadingClass}>2.1 Personalize Coaching</h4>
            <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-sm">
              <li>Generate custom workout plans</li>
              <li>Provide ongoing AI-based coaching</li>
              <li>Track your progress over time</li>
              <li>Recommend exercises and nutrition insights</li>
            </ul>
          </div>

          <div>
            <h4 className={subHeadingClass}>2.2 Improve the App</h4>
            <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-sm">
              <li>Analyze performance</li>
              <li>Identify bugs</li>
              <li>Optimize features</li>
              <li>Understand usage patterns</li>
            </ul>
          </div>

          <div>
            <h4 className={subHeadingClass}>2.3 Security and Abuse Prevention</h4>
            <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-sm">
              <li>Prevent excessive use of AI systems</li>
              <li>Protect accounts from unauthorized access</li>
              <li>Identify suspicious activity</li>
            </ul>
          </div>

          <div>
            <h4 className={subHeadingClass}>2.4 Communication</h4>
            <p className="text-muted-foreground text-sm mb-2">We may send:</p>
            <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-sm">
              <li>Login verification emails</li>
              <li>Important service updates</li>
              <li>Feature announcements</li>
            </ul>
            <p className="text-muted-foreground text-sm mt-2 font-medium">
              We do not send promotional spam.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3 */}
      <section>
        <h3 className={headingClass}>3. How We Share Your Information</h3>
        <p className={smallTextClass + " mb-3 font-medium"}>We do not sell your personal data.</p>
        <p className={smallTextClass + " mb-4"}>
          We only share information with trusted third-party providers who assist in operating Straiv:
        </p>

        <div className="space-y-4 ml-4">
          <div>
            <h4 className={subHeadingClass}>3.1 Supabase (Database & Authentication)</h4>
            <p className="text-muted-foreground text-sm mb-2">Supabase stores:</p>
            <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-sm">
              <li>User profile data</li>
              <li>Workout logs</li>
              <li>AI chat history</li>
              <li>Account authentication</li>
            </ul>
            <p className="text-muted-foreground text-sm mt-2">
              Supabase acts as a secure data processor.
            </p>
          </div>

          <div>
            <h4 className={subHeadingClass}>3.2 Google Gemini (AI Processing)</h4>
            <p className="text-muted-foreground text-sm mb-2">
              Straiv uses Google's Gemini models (including Gemini 2.5 Flash) to generate AI coaching responses.
            </p>
            <p className="text-muted-foreground text-sm mb-1 font-medium">What is shared with Gemini:</p>
            <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-sm">
              <li>The message you send to your AI coach</li>
              <li>Relevant context required for generating a meaningful reply</li>
            </ul>
            <p className="text-muted-foreground text-sm mb-1 mt-2 font-medium">What is NOT shared with Gemini:</p>
            <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-sm">
              <li>Your email</li>
              <li>Your account ID</li>
              <li>Personal identifiers</li>
              <li>Sensitive metadata</li>
              <li>Any information not necessary for generating an answer</li>
            </ul>
            <p className="text-muted-foreground text-sm mt-2">
              Gemini processes messages in real time and does not store your personal data permanently.
              (All processing follows Google AI safety and privacy policies.)
            </p>
          </div>

          <div>
            <h4 className={subHeadingClass}>3.3 Google / Apple Sign-In (Optional)</h4>
            <p className="text-muted-foreground text-sm mb-2">
              If you choose social login, these providers verify your identity and share:
            </p>
            <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-sm">
              <li>Name</li>
              <li>Email</li>
            </ul>
            <p className="text-muted-foreground text-sm mt-2">
              They do not provide us access to your password or any additional personal information.
            </p>
          </div>

          <div>
            <h4 className={subHeadingClass}>3.4 Analytics Providers</h4>
            <p className="text-muted-foreground text-sm">
              If analytics (e.g., Firebase) is enabled, anonymized usage statistics may be collected.
            </p>
          </div>
        </div>
      </section>

      {/* Section 4 */}
      <section>
        <h3 className={headingClass}>4. Data Retention</h3>
        <p className={smallTextClass + " mb-2"}>
          We retain data as long as your account is active.
        </p>
        <p className={smallTextClass + " mb-2"}>If you request deletion:</p>
        <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-sm">
          <li>All personal data is permanently erased within 30 days</li>
          <li>Workout logs, chat logs, profile data are removed</li>
          <li>Anonymized, non-identifiable statistics may remain</li>
        </ul>
      </section>

      {/* Section 5 */}
      <section>
        <h3 className={headingClass}>5. Your Rights</h3>
        <p className={smallTextClass + " mb-2"}>You may at any time:</p>
        <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-sm">
          <li>Access your personal data</li>
          <li>Update your information</li>
          <li>Download/export your data</li>
          <li>Request deletion</li>
          <li>Opt out of analytics tracking</li>
        </ul>
        <p className={smallTextClass + " mt-3"}>
          To exercise your rights, email us at <span className="font-medium text-foreground">circletester123@gmail.com</span>.
        </p>
      </section>

      {/* Section 6 */}
      <section>
        <h3 className={headingClass}>6. Data Security</h3>
        <p className={smallTextClass + " mb-2"}>
          We implement strict industry-standard protections:
        </p>
        <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-sm">
          <li>Encrypted database storage</li>
          <li>HTTPS-encrypted traffic</li>
          <li>Access controls</li>
          <li>Regular system audits</li>
        </ul>
        <p className={smallTextClass + " mt-3"}>
          No digital system is ever 100% risk-free, but we take all reasonable measures to protect your information.
        </p>
      </section>

      {/* Section 7 */}
      <section>
        <h3 className={headingClass}>7. Children's Privacy</h3>
        <p className={smallTextClass}>
          Straiv is not intended for children under 13 years of age.
          If we learn that data has been collected from a minor, we will delete it immediately.
        </p>
      </section>

      {/* Section 8 */}
      <section>
        <h3 className={headingClass}>8. Changes to This Privacy Policy</h3>
        <p className={smallTextClass}>
          We may occasionally update this policy.
          When significant changes occur, we will notify users in-app or on our website.
        </p>
      </section>

      {/* Section 9 */}
      <section>
        <h3 className={headingClass}>9. Contact Us</h3>
        <p className={smallTextClass + " mb-3"}>
          For privacy questions, concerns, or data deletion requests:
        </p>
        <div className="p-3 bg-muted/20 rounded-lg border border-border/30">
          <p className="font-medium text-foreground text-sm">Email: circletester123@gmail.com</p>
          <p className="text-xs text-muted-foreground mt-1">
            We respond within 48 hours.
          </p>
        </div>
      </section>
    </div>
  );
};
